/**
 * The wind barb convention, in one function, drawn to a canvas.
 *
 * A half barb per 5 kt, a full barb per 10, a pennant per 50, and a circle for
 * calm. That is the whole of it, and it is here rather than in the backend for
 * the same reason `LayerDef.label` is: the backend sends a speed and a
 * direction, which is what a barb *means*; how many ticks that draws is what a
 * barb *looks like*, and the two belong on opposite sides of the seam.
 *
 * **Registered images, not a sprite.** 21 canvases at 5 kt steps from 0 to
 * 100+, drawn once at `addImage` time, then `icon-image` picks one by the `k`
 * property the backend already rounded. No sprite asset to keep in step with a
 * table, no new dependency, and the convention lives in `ticksFor` where a
 * reader can check it against a textbook.
 *
 * Sizes go through `devicePixelRatio` or the glyphs are soft on a retina
 * display — `addImage` takes a `pixelRatio` and MapLibre scales accordingly.
 */

/** The step the backend rounds a speed to, and therefore the icon key step. */
export const GLYPH_STEP_KT = 5
/** Above this every glyph is the same; 100 kt at 10 m is already off the map. */
export const MAX_GLYPH_KT = 100

export interface BarbTicks {
  pennants: number
  full: number
  half: number
}

/**
 * How many of each tick a speed draws.
 *
 * Rounded to the nearest 5 kt first, which is the convention: a barb is a
 * coded value, not a continuous one, and 12 kt has always been drawn as 10.
 * The backend emits that rounded value as the icon key so the two halves
 * cannot disagree about it.
 */
export function ticksFor(knots: number): BarbTicks {
  const stepped = Math.max(0, Math.round(knots / GLYPH_STEP_KT) * GLYPH_STEP_KT)
  let left = Math.min(stepped, MAX_GLYPH_KT + GLYPH_STEP_KT)
  const pennants = Math.floor(left / 50)
  left -= pennants * 50
  const full = Math.floor(left / 10)
  left -= full * 10
  return { pennants, full, half: left >= 5 ? 1 : 0 }
}

/** Every icon key the backend can emit, so they are all registered up front. */
export function glyphKeys(): number[] {
  const out: number[] = []
  for (let kt = 0; kt <= MAX_GLYPH_KT; kt += GLYPH_STEP_KT) out.push(kt)
  return out
}

/** `barb-25`. One place, because the registrar and the expression both need it. */
export function glyphName(key: number): string {
  return `barb-${key}`
}

/** The geometry, in CSS pixels before the device ratio is applied. */
const SIZE = 30
const SHAFT = 22
const BARB_LEN = 9
const BARB_GAP = 4
const CALM_RADIUS = 3.5

export interface BarbImage {
  width: number
  height: number
  data: Uint8ClampedArray
  pixelRatio: number
}

/**
 * Draw one barb, pointing **up** — north, before `icon-rotate` turns it.
 *
 * The shaft runs from the plotted point (the bottom of the glyph, where the
 * observation is) upward, and the ticks hang off the top on the left-hand
 * side. `icon-rotate` is then the direction the wind comes *from*, which is
 * what the backend emits: an arrow drawn toward the origin of the wind is the
 * convention, and rotating a from-direction glyph by the from-direction is
 * what puts the ticks upwind.
 *
 * Returns null when there is no canvas — jsdom, or a headless context — so a
 * test can call the geometry without the layer needing a real GPU.
 */
export function drawBarb(knots: number, color: string, ratio = 1): BarbImage | null {
  if (!canRaster()) return null
  const canvas = makeCanvas(SIZE * ratio, SIZE * ratio)
  const ctx = canvas?.getContext('2d')
  if (!canvas || !ctx) return null

  ctx.scale(ratio, ratio)
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = 1.5
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  const cx = SIZE / 2
  const bottom = SIZE - 2
  const { pennants, full, half } = ticksFor(knots)

  if (pennants === 0 && full === 0 && half === 0) {
    // Calm: an open circle at the point, which is the convention and is also
    // the only thing that distinguishes "no wind" from "no barb here".
    ctx.beginPath()
    ctx.arc(cx, bottom - CALM_RADIUS, CALM_RADIUS, 0, Math.PI * 2)
    ctx.stroke()
    return toImage(ctx, canvas, ratio)
  }

  const top = bottom - SHAFT
  ctx.beginPath()
  ctx.moveTo(cx, bottom)
  ctx.lineTo(cx, top)
  ctx.stroke()

  let y = top
  for (let i = 0; i < pennants; i++) {
    // A filled triangle, base on the shaft.
    ctx.beginPath()
    ctx.moveTo(cx, y)
    ctx.lineTo(cx - BARB_LEN, y + BARB_GAP * 0.8)
    ctx.lineTo(cx, y + BARB_GAP * 1.6)
    ctx.closePath()
    ctx.fill()
    y += BARB_GAP * 1.9
  }
  for (let i = 0; i < full; i++) {
    ctx.beginPath()
    ctx.moveTo(cx, y)
    ctx.lineTo(cx - BARB_LEN, y + BARB_GAP * 0.9)
    ctx.stroke()
    y += BARB_GAP
  }
  if (half > 0) {
    // A half barb never sits at the very end of the shaft when there is
    // nothing else on it: 5 kt is drawn one tick in, so it cannot be mistaken
    // for a full barb foreshortened.
    if (pennants === 0 && full === 0) y += BARB_GAP
    ctx.beginPath()
    ctx.moveTo(cx, y)
    ctx.lineTo(cx - BARB_LEN / 2, y + BARB_GAP * 0.45)
    ctx.stroke()
  }
  return toImage(ctx, canvas, ratio)
}

/**
 * Whether this environment can rasterise at all, asked **once**.
 *
 * jsdom has an `HTMLCanvasElement` and no 2d rasteriser, and it logs a line
 * every time `getContext` is called. Registering 21 glyphs per mounted layer
 * per test file turns that into hundreds of lines of noise around a suite that
 * is passing. One probe answers it for the process, and the layer degrades to
 * no icons — which is exactly what `registry.test.ts` needs it to do.
 */
let raster: boolean | null = null

function canRaster(): boolean {
  if (raster !== null) return raster
  try {
    raster = makeCanvas(1, 1)?.getContext('2d') != null
  } catch {
    raster = false
  }
  return raster
}

function makeCanvas(width: number, height: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width))
  canvas.height = Math.max(1, Math.round(height))
  return canvas
}

function toImage(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  ratio: number,
): BarbImage | null {
  try {
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height)
    return { width: canvas.width, height: canvas.height, data: data.data, pixelRatio: ratio }
  } catch {
    // A context that exists and cannot read back. The layer degrades to no
    // icons rather than throwing, and `ticksFor` — which is the part worth
    // testing — needs none of this.
    return null
  }
}

/** Tests only: the raster probe is a module-level answer. */
export function _resetRasterProbe(): void {
  raster = null
}
